import { defineStore } from "pinia";
import { computed, ref } from "vue";
import api, { deserializeLeague } from "@/services/api";
import { LeagueDTO } from "../../../dto/leagueDTO";
import { GLOBAL_LEAGUE_ID, isLeagueInactive } from "../../../model/league";
import { Temporal } from "@js-temporal/polyfill";

/**
 * Manages the player's league context.
 *
 * This store holds ONLY shared UI state that multiple unrelated components
 * need to read simultaneously:
 *   - Which league is currently selected (drives all scoped API calls)
 *   - The list of available leagues (needed by NavBar selector + dashboard)
 *
 * Remote data (dashboard KPIs, contracts, leaderboard, notifications, the
 * player's team) is fetched via TanStack Query composables in
 * src/composables/ and does NOT live here.
 */
export const useLeagueStore = defineStore("league", () => {
  // ── State ──────────────────────────────────────────────────────────────────
  const currentLeague = ref<LeagueDTO>();
  const availableLeagues = ref<LeagueDTO[]>([]);
  const isLoading = ref(false);
  // Whether the league list has been fetched *successfully* at least once this
  // session. An empty `availableLeagues` is ambiguous on its own — it reads the
  // same before the first fetch, after a failed one, and for a player who
  // genuinely has no team — so this flag is what lets callers (e.g. the "create
  // your team" prompt) tell them apart instead of firing on a state they only
  // believe because the request never came back.
  const hasLoaded = ref(false);
  const error = ref<string | null>(null);

  // ── Getters ────────────────────────────────────────────────────────────────

  const currentLeagueId = computed(() => currentLeague.value?.id ?? null);

  const currentLeagueName = computed(
    () => currentLeague.value?.title ?? "No League Selected"
  );

  // Whether the player has completed onboarding: a team in the Global League,
  // the one membership the first run creates. Deliberately *not* "is in any
  // league" — once a player can join further leagues, someone could hold a team
  // elsewhere while still owing their Global League team, and that player is
  // exactly who the re-prompt is for. Undefined until the first fetch resolves,
  // so callers can distinguish "no team" from "not known yet".
  const hasGlobalTeam = computed(() =>
    hasLoaded.value
      ? availableLeagues.value.some((lg) => lg.id === GLOBAL_LEAGUE_ID)
      : undefined
  );

  /**
   * Leagues still worth playing — what the NavBar picker offers and the
   * "mine" grid on /leagues renders. `availableLeagues` itself stays
   * unfiltered (other readers, e.g. the league-detail placeholder, still
   * need to find an ended league by id), so this and `endedLeagues` are the
   * split every selectability decision should read instead.
   */
  const activeLeagues = computed(() =>
    availableLeagues.value.filter(
      (l) => !isLeagueInactive(l, Temporal.Now.instant())
    )
  );

  /**
   * The other half of the same split: leagues whose season is over or that
   * their admin closed early. Kept visible rather than dropped — a league
   * going inactive doesn't erase the fact that the player was in it, it only
   * takes it out of the set a click can lead back into.
   */
  const endedLeagues = computed(() =>
    availableLeagues.value.filter((l) =>
      isLeagueInactive(l, Temporal.Now.instant())
    )
  );

  // ── Helpers ────────────────────────────────────────────────────────────────

  /**
   * Validate `currentLeague` against the leagues still selectable.
   * Resolved against `activeLeagues`, not the raw fetch: an ended league has
   * already left the NavBar picker, so a selection still pointing at one — a
   * stale localStorage entry, or a league that simply finished under the
   * player while it was selected — must not survive the next fetch. Falls
   * back to the first still-active league, or clears the selection entirely
   * if none is left, rather than fabricating a placeholder league to point
   * at (`currentLeagueName` already has its own "No League Selected"
   * fallback for that). `currentLeague` being reactive is what lets every
   * league-scoped screen notice the reassignment instead of going on serving
   * the dead one.
   */
  function _resolveCurrentLeague(): LeagueDTO | undefined {
    const candidate = currentLeague.value;
    const found = candidate
      ? (activeLeagues.value.find((l) => l.id === candidate.id) ?? null)
      : null;

    const resolved = found ?? activeLeagues.value[0];

    // Assigned even when the id is unchanged, which it did not used to be. The
    // restored value is a `localStorage` snapshot of a league as it looked
    // whenever it was last selected, so keeping it because the *id* still
    // matches keeps every one of its stale fields too — its team count, and now
    // its Language Scale Factor, which a league saved before that field existed
    // does not carry at all. That would price a whole market off an undefined
    // factor. The fetched object is the same league, only current.
    currentLeague.value = resolved;

    if (resolved) {
      localStorage.setItem("currentLeague", JSON.stringify(resolved));
    } else {
      // Nothing left to fall into — clear rather than fabricate a placeholder
      // league (`currentLeagueName` has its own "No League Selected" fallback).
      localStorage.removeItem("currentLeague");
    }

    return resolved;
  }

  // ── Actions ────────────────────────────────────────────────────────────────

  /**
   * Fetch the list of available leagues from the API.
   * Always validates the current selection against the fresh list and falls
   * back to the first league when the stored id is not found.
   */
  async function fetchLeagues() {
    isLoading.value = true;
    error.value = null;
    try {
      availableLeagues.value = await api.leagues.getAll();
      _resolveCurrentLeague();
      // Success only: a failed fetch leaves `availableLeagues` empty, which is
      // indistinguishable from "player has no team". Marking that as loaded
      // would tell an established player they own nothing — see `hasGlobalTeam`.
      hasLoaded.value = true;
    } catch (err) {
      error.value =
        err instanceof Error ? err.message : "Failed to fetch leagues";
      console.error("Failed to fetch leagues:", err);
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Switch the active league.
   * Persists the selection to localStorage so it survives a page refresh.
   * Components that depend on league-scoped data (via useQuery with the
   * leagueId in the query key) will automatically refetch when this changes.
   */
  function setCurrentLeague(league: LeagueDTO) {
    currentLeague.value = league;
    localStorage.setItem("currentLeague", JSON.stringify(league));
  }

  /**
   * Bootstrap: restore persisted league then fetch and validate the full list.
   * Called once from NavBar on mount.
   *
   * Order of operations:
   *  1. Optimistically restore from localStorage so the UI has something to
   *     show immediately (avoids a blank league selector on first paint).
   *  2. Fetch the authoritative list from the API.
   *  3. `fetchLeagues` calls `_resolveCurrentLeague`, which validates the
   *     optimistic value against the real list and corrects it if stale.
   */
  async function initialize() {
    const saved = localStorage.getItem("currentLeague");
    if (saved) {
      try {
        // Optimistic restore — may be stale; will be validated after fetch.
        const parsed = JSON.parse(saved);
        currentLeague.value = deserializeLeague(parsed);
      } catch {
        // Corrupt localStorage entry — ignore; fetchLeagues will set a default.
        localStorage.removeItem("currentLeague");
      }
    }
    await fetchLeagues();
  }

  return {
    // State
    currentLeague,
    availableLeagues,
    isLoading,
    hasLoaded,
    error,
    // Getters
    currentLeagueId,
    currentLeagueName,
    hasGlobalTeam,
    activeLeagues,
    endedLeagues,
    // Actions
    setCurrentLeague,
    fetchLeagues,
    initialize,
  };
});
